import * as THREE from 'three'
import type { LedShape, VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { PRIMITIVE_TEXTURE_GLSL } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise, POINT_SPRITE_GLSL, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * LED screen: a grid of points where each point is an LED that takes the color
 * of the matching pixel of the selected media (video/image) — the video plays
 * "pixelated" as LEDs.
 *
 * Shapes (params.ledShape):
 *  - 'plane'    : flat 16:9 panel.
 *  - 'curve'    : concave 180° curved wall (panoramic).
 *  - 'sphere'   : 360° sphere (camera inside, equirectangular video).
 *  - 'cylinder' : 360° LED wall (camera inside).
 *
 * The video can wrap once ('full'), tile ('repeat'), mirror, or kaleidoscope
 * across the surface (params.contentWrap + contentTiles). Bass/beat give the
 * LEDs relief and a brightness flash; Zone reaction (when on) localizes the
 * deformation to the moving shapes.
 */
export class LedPanel extends BasePrimitive {
  object3D: THREE.Points
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
      vertexShader: /* glsl */ `
        attribute vec2 aUv;
        attribute vec3 aNormal;
        uniform float uTime, uBass, uBeat, uReactivity, uBassPunch, uPixelRatio, uPrimitiveMediaActive, uLedSize, uPointScale;
        uniform float uTiles, uWrapMode;  // 0 full, 1 repeat, 2 mirror, 3 kaleido
        uniform float uReliefSign;
        uniform sampler2D uPrimitiveMedia;
        varying vec2 vUv;
        varying float vLum;
        varying float vFacing;
        varying vec3 vDir;
        ${snoise}
        ${ZONE_GLSL_COMMON}
        // Wrap the video over the surface: a single wrap, a tiled mosaic, a
        // seamless mirror, or an angular kaleidoscope.
        vec2 wrapUv(vec2 uv) {
          if (uWrapMode < 0.5) return uv;
          if (uWrapMode < 1.5) return fract(uv * uTiles);           // repeat
          if (uWrapMode < 2.5) {                                    // mirror
            vec2 f = fract(uv * uTiles * 0.5) * 2.0;
            return vec2(f.x < 1.0 ? f.x : 2.0 - f.x, f.y < 1.0 ? f.y : 2.0 - f.y);
          }
          // kaleidoscope: mirror into uTiles angular wedges around the center.
          vec2 p = uv - 0.5;
          float r = length(p);
          float ang = atan(p.y, p.x);
          float seg = 6.2831853 / max(1.0, uTiles);
          ang = abs(mod(ang, seg) - seg * 0.5);
          return clamp(vec2(cos(ang), sin(ang)) * r + 0.5, 0.0, 1.0);
        }
        void main() {
          vec2 sampleUv = wrapUv(aUv);
          vUv = sampleUv;
          vec3 c = texture2D(uPrimitiveMedia, sampleUv).rgb;
          float lum = dot(c, vec3(0.299, 0.587, 0.114)) * uPrimitiveMediaActive;
          vLum = lum;
          // When zone reaction is on the base relief is frozen — only zones react.
          float gate = 1.0 - uZoneEnabled;
          float audio = (uBass * uBassPunch + uBeat * 0.8) * uReactivity;
          vec3 pos = position;
          if (uReliefSign < 0.0) {
            // Wrap shapes (camera at the center): a radial push isn't visible, so
            // we displace TANGENTIALLY over the surface — noise waves modulated by
            // the music + pixel brightness — plus a slight pull toward the center.
            vec3 nrm = normalize(position);
            vec3 t1 = normalize(cross(nrm, vec3(0.0, 1.0, 0.0)) + vec3(1e-4));
            vec3 t2 = cross(nrm, t1);
            float amp = audio * (0.3 + lum * 1.2) * length(position) * 0.05 * gate;
            float nx = snoise(position * 0.7 + vec3(0.0, 0.0, uTime * 0.9));
            float ny = snoise(position * 0.7 + vec3(13.0, 7.0, uTime * 0.9));
            pos += (t1 * nx + t2 * ny) * amp;
            pos -= nrm * lum * audio * 0.5 * gate;
          } else {
            // Plane / curve: relief toward the camera (visible from the side).
            pos += aNormal * lum * audio * 1.6 * gate;
          }
          // Zone reaction: shapes ride the panel UV and push the LEDs along the normal.
          vec3 zdir = zoneDirFromUV(aUv);
          vDir = zdir;
          pos += aNormal * zoneDisp(zdir) * 1.2;
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          vec3 nv = normalize(normalMatrix * aNormal);
          vFacing = dot(nv, normalize(-mv.xyz));
          gl_Position = projectionMatrix * mv;
          float px = uLedSize * (0.45 + lum * (1.2 + audio * 2.5) + uBeat * 0.4 * uReactivity);
          gl_PointSize = clamp(px * (6.0 / -mv.z), 1.0, 56.0) * uPixelRatio * uPointScale;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uCullBack, uBass, uBeat, uReactivity;
        varying vec2 vUv;
        varying float vLum;
        varying float vFacing;
        varying vec3 vDir;
        // uPrimitiveMedia + uPrimitiveMediaActive are declared by PRIMITIVE_TEXTURE_GLSL.
        ${PRIMITIVE_TEXTURE_GLSL}
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          if (uCullBack > 0.5 && vFacing < 0.0) discard;
          float core = pointMask();
          if (core < 0.01) discard;
          vec4 zc = zoneColor(vDir);
          if (uPrimitiveMediaActive < 0.5) {
            // No media: faint grid + any zone shapes on top.
            vec3 base = mix(vec3(0.08), zc.rgb, zc.a);
            gl_FragColor = vec4(base, max(core * 0.18, zc.a * core));
            return;
          }
          // Full-colour video by default; recolor through the palette when Colors is on.
          vec3 col = texture2D(uPrimitiveMedia, vUv).rgb;
          col = uReActive > 0.5 ? reColor(col) : col;
          // Brightness flash with bass/beat.
          col *= 1.0 + (uBass * 0.4 + uBeat * 0.7) * uReactivity;
          float on = smoothstep(0.02, 0.18, vLum);
          float alpha = core * (0.15 + on * 0.85);
          col = mix(col, zc.rgb, zc.a);
          gl_FragColor = vec4(col, max(alpha, zc.a * core));
        }
      `,
    })
    // Material-specific uniforms (not part of the shared set).
    this.material.uniforms.uLedSize = { value: 5.0 }
    this.material.uniforms.uCullBack = { value: 0 }
    this.material.uniforms.uTiles = { value: 1 }
    this.material.uniforms.uWrapMode = { value: 0 }
    this.material.uniforms.uReliefSign = { value: 1 }

    this.object3D = new THREE.Points(new THREE.BufferGeometry(), this.material)
    this.object3D.frustumCulled = false
    this.rebuild({ density: 1, ledShape: 'plane' } as VisualParams)
  }

  update(params: VisualParams): void {
    const shape = params.ledShape ?? 'plane'
    const wrapShape = shape === 'sphere' || shape === 'cylinder'
    // Wrap shapes are seen from the inside: displace tangentially, not radially.
    this.material.uniforms.uReliefSign.value = wrapShape ? -1 : 1
    const wrap = params.contentWrap ?? 'full'
    const mode = wrap === 'full' ? 0 : wrap === 'repeat' ? 1 : wrap === 'mirror' ? 2 : 3
    this.material.uniforms.uWrapMode.value = mode
    this.material.uniforms.uTiles.value =
      mode === 0 ? 1 : Math.max(1, Math.round(params.contentTiles ?? 1))
  }

  rebuild(params: VisualParams): void {
    const shape: LedShape = params.ledShape ?? 'plane'
    const d = params.density ?? 1
    if (shape === 'sphere') this.buildSphere(d, params.sphereRadius ?? 8)
    else if (shape === 'cylinder') this.buildCylinder(d, params.sphereRadius ?? 8)
    else if (shape === 'curve') this.buildCurve(d)
    else this.buildPlane(d)
  }

  private setGeometry(
    positions: Float32Array,
    uvs: Float32Array,
    normals: Float32Array,
    ledSize: number,
  ) {
    this.material.uniforms.uLedSize.value = ledSize
    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aUv', new THREE.BufferAttribute(uvs, 2))
    geo.setAttribute('aNormal', new THREE.BufferAttribute(normals, 3))
    geo.computeBoundingSphere()
  }

  private buildPlane(d: number) {
    const cols = Math.max(16, Math.floor(160 * Math.sqrt(d)))
    const rows = Math.floor((cols * 9) / 16)
    const width = 8
    const height = (width * 9) / 16
    const n = cols * rows
    const positions = new Float32Array(n * 3)
    const uvs = new Float32Array(n * 2)
    const normals = new Float32Array(n * 3)
    let i = 0
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const u = c / (cols - 1)
        const v = r / (rows - 1)
        positions[i * 3] = (u - 0.5) * width
        positions[i * 3 + 1] = (v - 0.5) * height
        positions[i * 3 + 2] = 0
        normals[i * 3] = 0
        normals[i * 3 + 1] = 0
        normals[i * 3 + 2] = 1
        uvs[i * 2] = u
        uvs[i * 2 + 1] = v
        i++
      }
    }
    this.setGeometry(positions, uvs, normals, (width / cols) * 95)
  }

  private buildCurve(d: number) {
    const cols = Math.max(24, Math.floor(200 * Math.sqrt(d)))
    const rows = Math.floor((cols * 9) / 16)
    const R = 5.5
    const height = 5
    const arc = Math.PI // 180°
    const n = cols * rows
    const positions = new Float32Array(n * 3)
    const uvs = new Float32Array(n * 2)
    const normals = new Float32Array(n * 3)
    let i = 0
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const u = c / (cols - 1)
        const v = r / (rows - 1)
        const theta = (u - 0.5) * arc // -90°..+90°
        const sx = Math.sin(theta)
        const cz = Math.cos(theta)
        // Concave toward the camera: center near (z~0), wings back (z~-R).
        positions[i * 3] = sx * R
        positions[i * 3 + 1] = (v - 0.5) * height
        positions[i * 3 + 2] = cz * R - R
        normals[i * 3] = -sx
        normals[i * 3 + 1] = 0
        normals[i * 3 + 2] = -cz
        uvs[i * 2] = u
        uvs[i * 2 + 1] = v
        i++
      }
    }
    this.setGeometry(positions, uvs, normals, ((arc * R) / cols) * 95)
  }

  private buildCylinder(d: number, R: number) {
    // 360° LED wall (camera inside). Full ring in X-Z, height in Y.
    const cols = Math.min(900, Math.max(32, Math.floor(28 * R * Math.sqrt(d))))
    const height = R * 1.4
    const rows = Math.max(8, Math.floor((cols * height) / (2 * Math.PI * R)))
    const n = cols * rows
    const positions = new Float32Array(n * 3)
    const uvs = new Float32Array(n * 2)
    const normals = new Float32Array(n * 3)
    let i = 0
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const u = c / (cols - 1)
        const v = r / (rows - 1)
        const lon = (u - 0.5) * Math.PI * 2 // front at u=0.5
        const cx = Math.cos(lon)
        const sz = Math.sin(lon)
        positions[i * 3 + 0] = cx * R
        positions[i * 3 + 1] = (v - 0.5) * height
        positions[i * 3 + 2] = sz * R
        normals[i * 3 + 0] = cx
        normals[i * 3 + 1] = 0
        normals[i * 3 + 2] = sz
        uvs[i * 2] = u
        uvs[i * 2 + 1] = v
        i++
      }
    }
    this.setGeometry(positions, uvs, normals, ((Math.PI * 2 * R) / cols) * 95)
  }

  private buildSphere(d: number, R: number) {
    // LED count grows with the radius (≈ constant areal density), so a big
    // sphere reads as a huge distant dome and a small one as a close cluster.
    const cols = Math.min(900, Math.max(32, Math.floor(28 * R * Math.sqrt(d)))) // longitude
    const rows = Math.floor(cols / 2) // latitude (equirectangular 2:1)
    const n = cols * rows
    const positions = new Float32Array(n * 3)
    const uvs = new Float32Array(n * 2)
    const normals = new Float32Array(n * 3)
    let i = 0
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const u = c / (cols - 1)
        const v = r / (rows - 1)
        const lon = (u - 0.5) * Math.PI * 2 // -180°..180° (front at u=0.5)
        const lat = (v - 0.5) * Math.PI // -90°..+90°
        const cosLat = Math.cos(lat)
        const nx = cosLat * Math.sin(lon)
        const ny = Math.sin(lat)
        const nz = cosLat * Math.cos(lon)
        positions[i * 3] = nx * R
        positions[i * 3 + 1] = ny * R
        positions[i * 3 + 2] = nz * R
        normals[i * 3] = nx
        normals[i * 3 + 1] = ny
        normals[i * 3 + 2] = nz
        uvs[i * 2] = u
        uvs[i * 2 + 1] = v
        i++
      }
    }
    this.setGeometry(positions, uvs, normals, ((Math.PI * 2 * R) / cols) * 95)
  }
}
